import { MenuCollapseIcon, MenuDeleteIcon } from 'images'
import React, { useCallback, useState } from 'react'
import { useLocation } from 'react-router'

import { css } from 'design'
import * as AlertDialog from 'primitives/alert_dialog'
import { Button, buttonStyles } from 'primitives/button'
import { Card } from 'primitives/card'
import { Stack } from 'primitives/containers'
import * as DropdownMenu from 'primitives/dropdownmenu'
import { cn } from 'util/cn'

import { CheckIcon } from '../../images'

export type SpamOptionsDialogProps = {
  showDialog: boolean
  setDialogVisible: React.Dispatch<React.SetStateAction<boolean>>
  confirmAction: any
  dialogAction: any
}

export const SpamOptionsDialog = ({
  showDialog,
  setDialogVisible,
  confirmAction,
  dialogAction,
}: SpamOptionsDialogProps) => {
  const search = useLocation().search
  const actionedHandle = new URLSearchParams(search).get('actioned_handle')
  const action = dialogAction

  const [selectedOption, setSelectedOption] = useState<string>('Spam')

  const hideDialog = useCallback(() => {
    setDialogVisible(false)
  }, [setDialogVisible])

  const handleConfirm = useCallback(() => {
    confirmAction(selectedOption)
  }, [confirmAction, selectedOption])

  return (
    <>
      <AlertDialog.Root open={showDialog}>
        <AlertDialog.Overlay onClick={hideDialog} />
        <AlertDialog.Content
          onPointerDownOutside={hideDialog}
          css={{ padding: '0px' }}
        >
          <AlertDialog.Title css={{ padding: '$4 $4 0 $4' }}>
            <Stack
              direction="row"
              gap="3"
              css={{
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span>Confirm Action</span>
              <Button
                className={menuButtonStyles()}
                aria-label="Close Dialog"
                onClick={hideDialog}
              >
                <MenuDeleteIcon />
              </Button>
            </Stack>
          </AlertDialog.Title>
          <section className={dialogBodyStyles()}>
            <Card
              className={cardStyles({
                variant: 'enableDestroy',
              })}
            >
              <Stack direction={'column'} gap="3">
                <Stack direction="column" gap="2" className={textStackStyles()}>
                  <p>
                    Please let us know why you are {action}ing {actionedHandle}
                  </p>
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger
                      className={cn(
                        dropdownTriggerStyles(),
                        buttonStyles(),
                      )}
                      aria-describedby={'spam-options-selector-reason'}
                    >
                      <>
                        {selectedOption}
                        <MenuCollapseIcon className={rotate180Styles()} />
                      </>
                    </DropdownMenu.Trigger>

                    <DropdownMenu.Content
                      className="topic__calls__deviceselectors"
                      sideOffset={4}
                      align="start"
                      css={{
                        minWidth: 250,
                      }}
                    >
                      <DropdownMenu.Label>
                        Reason for {action}ing {actionedHandle}
                      </DropdownMenu.Label>

                      <DropdownMenu.RadioGroup
                        value={selectedOption || undefined}
                        onValueChange={(e: string) => setSelectedOption(e)}
                      >
                        <DropdownMenu.RadioItem key={'1'} value={'Spam'}>
                          <DropdownMenu.ItemIndicator>
                            <CheckIcon />
                          </DropdownMenu.ItemIndicator>
                          Spam
                        </DropdownMenu.RadioItem>
                        <DropdownMenu.RadioItem
                          key={'2'}
                          value={'Unknown Person'}
                        >
                          <DropdownMenu.ItemIndicator>
                            <CheckIcon />
                          </DropdownMenu.ItemIndicator>
                          Unknown Person
                        </DropdownMenu.RadioItem>
                        <DropdownMenu.RadioItem key={'3'} value={'Offensive'}>
                          <DropdownMenu.ItemIndicator>
                            <CheckIcon />
                          </DropdownMenu.ItemIndicator>
                          Offensive
                        </DropdownMenu.RadioItem>
                        <DropdownMenu.RadioItem key={'4'} value={'Other'}>
                          <DropdownMenu.ItemIndicator>
                            <CheckIcon />
                          </DropdownMenu.ItemIndicator>
                          Other
                        </DropdownMenu.RadioItem>
                      </DropdownMenu.RadioGroup>
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                </Stack>

                <Stack
                  direction={'row'}
                  gap={'4'}
                  className={buttonStackStyles()}
                >
                  <Button
                    onClick={hideDialog}
                    className={cn(
                      'topic__toolbarbutton',
                      removeButtonStyles(),
                      buttonPadding(),
                    )}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    variant={'primary'}
                    className={cn('topic__toolbarbutton', buttonPadding())}
                  >
                    Confirm
                  </Button>
                </Stack>
              </Stack>
            </Card>
          </section>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </>
  )
}

const removeButtonStyles = css({
  backgroundColor: '$grey200 !important',
  width: 'fit-content',
})

const buttonPadding = css({
  padding: '0 $4',
})

const menuButtonStyles = css({
  background: 'transparent !important',
  border: '1px solid transparent',
  justifyContent: 'left',
  '&:hover': {
    background: '$grey50 !important',
    border: '1px solid transparent',
  },
  '.topic__theme--dark &': {
    border: '1px solid transparent',
    '&:hover': {
      border: '1px solid transparent',
    },
  },
})

const dialogBodyStyles = css({
  padding: '$4',
  borderTop: '1px solid $grey100',
  margin: '0',
})

const buttonStackStyles = css({
  alignItems: 'flex-end ',
  justifyContent: 'center',
  marginLeft: '$5',
  marginRight: '0',
  '@lg': {
    maxWidth: 'unset',
  },
})

const textStackStyles = css({
  alignItems: 'left',
  justifyContent: 'center',
})

const cardStyles = css({
  padding: '$3',

  variants: {
    variant: {
      destroy: {
        background: '$red500',
        color: 'white',
      },
      enableDestroy: {
        background: '$grey100',
      },
    },
  },
  borderRadius: '$3',
})

const rotate180Styles = css({
  transform: 'rotate(180deg)',
})

const dropdownTriggerStyles = css({
  height: '100%',
  justifyContent: 'space-between',
  padding: '$2 $3',
  textAlign: 'left',
  border: '1px solid $grey400 !important',
})
